import React from 'react'

type AgentType = 'external' | 'hosted'

interface BannerConfig {
  bg: string
  border: string
  color: string
  icon: string
  title: string
  description: string
}

// What each review status means for the creator — most importantly whether the
// agent is answering in chats/discussions yet. Colours match AgentStatusBadge.
function getConfig(status: string, agentType: AgentType | undefined): BannerConfig {
  switch (status) {
    case 'draft':
      return {
        bg: 'rgba(124,58,237,0.08)', border: 'rgba(124,58,237,0.3)', color: '#7c3aed', icon: '📝',
        title: 'Draft',
        description: agentType === 'hosted'
          ? 'Not live yet. Upload at least one knowledge document, wait for it to finish processing, then click Submit for Review.'
          : 'Not live yet. Finish setup, then click Submit for Review.',
      }
    case 'pending_review':
      return {
        bg: 'rgba(234,179,8,0.09)', border: 'rgba(234,179,8,0.35)', color: 'var(--warning)', icon: '⏳',
        title: 'Pending review',
        description: 'Submitted — an admin is reviewing your agent. It will not answer in chats or discussions until it is approved. You will be notified when a decision is made.',
      }
    case 'active':
      return {
        bg: 'rgba(34,197,94,0.09)', border: 'rgba(34,197,94,0.3)', color: 'var(--success)', icon: '✅',
        title: 'Active',
        description: 'Approved and live — your agent is answering in chats and relevant discussions.',
      }
    case 'rejected':
      return {
        bg: 'rgba(239,68,68,0.09)', border: 'rgba(239,68,68,0.3)', color: 'var(--error)', icon: '❌',
        title: 'Rejected',
        description: 'Not approved, so it is not answering anywhere. Fix the issues in the reviewer note and resubmit.',
      }
    default:
      return {
        bg: 'rgba(107,114,128,0.09)', border: 'rgba(107,114,128,0.3)', color: '#9ca3af', icon: '○',
        title: 'Inactive',
        description: 'Not answering in chats or discussions. Resubmit it to bring it back for review.',
      }
  }
}

/**
 * Full-width status banner for an agent: the status, what it means (is it
 * replying in chats yet?), and the reviewer's note when there is one.
 */
export const AgentStatusBanner: React.FC<{
  status: string
  agentType?: AgentType
  reviewNote?: string | null
  style?: React.CSSProperties
}> = ({ status, agentType, reviewNote, style }) => {
  const c = getConfig(status, agentType)
  // Only rejections/inactivations carry a note worth surfacing; an approved
  // agent's old note would just be noise.
  const showNote = !!reviewNote && (status === 'rejected' || status === 'inactive')
  return (
    <div
      role="status"
      style={{
        display: 'flex', gap: '0.65rem', alignItems: 'flex-start',
        background: c.bg, border: `1px solid ${c.border}`, borderLeft: `3px solid ${c.color}`,
        borderRadius: 8, padding: '0.6rem 0.85rem', ...style,
      }}
    >
      <span style={{ fontSize: '1rem', lineHeight: 1.3 }}>{c.icon}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '0.8rem', fontWeight: 700, color: c.color }}>Status: {c.title}</div>
        <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', lineHeight: 1.45, marginTop: '0.1rem' }}>
          {c.description}
        </div>
        {showNote && (
          <div style={{ fontSize: '0.76rem', color: 'var(--text-primary)', marginTop: '0.35rem', wordBreak: 'break-word' }}>
            <strong>Reviewer note:</strong> {reviewNote}
          </div>
        )}
      </div>
    </div>
  )
}
